import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Banknote, MapPin, Clock, ShoppingBag, ArrowLeft, CheckCircle2, Gift, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";
import EscaparateBanner from "@/components/cart/EscaparateBanner";
import { LOCATIONS, PaymentMethod } from "@/types/orders";

const Checkout = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { items, total, orderType, tableNumber, locationCode, sessionMode, isValidSession, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get encargo details first to determine payment default
  const encargoDetailsEarly = sessionStorage.getItem("encargoDetails");
  const isEncargoOrder = orderType === "encargo" || !!encargoDetailsEarly;
  
  // For encargos, force online payment by default
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(isEncargoOrder ? "online" : "local");

  // Get encargo details first to check if this is an encargo order
  const encargoDetails = sessionStorage.getItem("encargoDetails");
  const encargo = encargoDetails ? JSON.parse(encargoDetails) : null;
  const isEncargo = orderType === "encargo" || !!encargo;

  // Check if in Escaparate mode (blocked from ordering)
  // EXCEPTION: Encargos are ALWAYS allowed even in escaparate mode
  const isEscaparateMode = !isEncargo && sessionMode === "escaparate" && !isValidSession;

  // Check if this is a free order (total = 0€) - Test Mode
  const isFreeOrder = total === 0;

  // SECURITY: Validate table number for mesa orders - Block if orderType is 'mesa' but no tableNumber
  const hasMesaError = useMemo(() => {
    const storedMesa = sessionStorage.getItem('tableNumber');
    const storedOrderType = sessionStorage.getItem('orderType');
    // If orderType is mesa (from context or storage) but no tableNumber exists
    const isMesaOrder = orderType === "mesa" || storedOrderType === "mesa";
    const hasTable = tableNumber || (storedMesa && storedMesa !== 'null');
    return isMesaOrder && !hasTable;
  }, [orderType, tableNumber]);

  // Location info for encargo
  const location = encargo ? LOCATIONS.find(l => l.id === encargo.location) : null;

  // Determine location code for the order - prioritize cart context, then encargo, then session
  const orderLocationCode = locationCode || encargo?.location || sessionStorage.getItem("locationCode") || null;
  
  console.log("[Checkout] Order location code:", orderLocationCode, "from cart:", locationCode, "from encargo:", encargo?.location);
  console.log("[Checkout] Session mode:", sessionMode, "isValidSession:", isValidSession);
  console.log("[Checkout] Is free order (Test Mode):", isFreeOrder, "Total:", total);

  const formatPrice = (price: number) => {
    return price.toFixed(2).replace(".", ",") + "€";
  };

  const handlePayLocal = async () => {
    // SECURITY: Block local payment in Escaparate mode
    if (isEscaparateMode) {
      toast({
        title: "Acceso restringido",
        description: "Para pedir, escanea el código QR de tu mesa o mostrador en el local.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      // Use server-side validation for order creation
      const orderData = {
        order_type: (isEncargo ? "encargo" : (orderType || "llevar")) as "mesa" | "llevar" | "encargo",
        status: "pendiente" as const,
        payment_method: "local" as const,
        payment_status: "pago_en_tienda" as const,
        table_number: tableNumber,
        customer_name: encargo?.customerName,
        customer_phone: encargo?.contactType === "phone" ? encargo?.contactValue : null,
        customer_email: encargo?.contactType === "email" ? encargo?.contactValue : null,
        pickup_date: encargo?.pickupDate,
        pickup_time: encargo?.pickupTime,
        pickup_location: encargo?.location,
        observations: encargo?.observations,
        location_code: orderLocationCode, // Pass location code for server lookup
      };

      // Send items to server for validation and order creation
      const { data, error } = await supabase.functions.invoke("validate-order", {
        body: {
          items: items.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            category: item.category,
            removedIngredients: item.removedIngredients,
          })),
          orderData,
        },
      });

      if (error) throw error;

      if (data.priceError) {
        toast({
          title: "Error de precios",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      if (!data.success) {
        throw new Error(data.error || "Error al crear el pedido");
      }

      // Clear cart and encargo details
      clearCart();
      sessionStorage.removeItem("encargoDetails");

      toast({
        title: "¡Pedido realizado!",
        description: isEncargo 
          ? "Te contactaremos para confirmar los detalles." 
          : `Pedido #${data.order.order_number} registrado. Paga en caja.`,
      });

      // Redirect to order status page
      navigate(`/estado-pedido/${data.order.id}`);
    } catch (error) {
      console.error("Error creating order:", error);
      toast({
        title: "Error",
        description: "No se pudo procesar el pedido. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayOnline = async () => {
    // Online payment is allowed even in Escaparate mode (as a fallback)
    // But we still validate the session for local payments
    
    setIsProcessing(true);
    try {
      // Use server-side validation for order creation with 'esperando_pago' status
      const orderData = {
        order_type: (isEncargo ? "encargo" : (orderType || "llevar")) as "mesa" | "llevar" | "encargo",
        status: "esperando_pago" as const,
        payment_method: "online" as const,
        payment_status: "pendiente" as const,
        table_number: tableNumber,
        customer_name: encargo?.customerName,
        customer_phone: encargo?.contactType === "phone" ? encargo?.contactValue : null,
        customer_email: encargo?.contactType === "email" ? encargo?.contactValue : null,
        pickup_date: encargo?.pickupDate,
        pickup_time: encargo?.pickupTime,
        pickup_location: encargo?.location,
        observations: encargo?.observations,
        location_code: orderLocationCode, // Pass location code for server lookup
      };

      // Send items to server for validation and order creation
      const { data, error } = await supabase.functions.invoke("validate-order", {
        body: {
          items: items.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            category: item.category,
            removedIngredients: item.removedIngredients,
          })),
          orderData,
        },
      });

      if (error) throw error;

      if (data.priceError) {
        toast({
          title: "Error de precios",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      if (!data.success) {
        throw new Error(data.error || "Error al crear el pedido");
      }

      const order = data.order;

      console.log('🔥 CHECKOUT: Preparando items para Stripe');
      console.log('📦 Items del carrito:', JSON.stringify(items, null, 2));

      // Format items with extras description for Stripe
      // Format items for Stripe with correct field names
      const lineItems = items.map((item, index) => {
        console.log(`\n🎂 Formateando item ${index + 1}: ${item.name}`);
        
        // Build description from selectedExtras (customizations)
        const extrasDescription = item.selectedExtras && item.selectedExtras.length > 0
          ? item.selectedExtras.map(e => `${e.groupTitle}: ${e.optionLabel}`).join(', ')
          : '';
        
        // Build description from removedIngredients (legacy)
        const removedDescription = item.removedIngredients && item.removedIngredients.length > 0
          ? `Sin: ${item.removedIngredients.join(', ')}`
          : '';
        
        // Combine descriptions
        const descriptions = [extrasDescription, removedDescription].filter(Boolean);
        const description = descriptions.length > 0 ? `(${descriptions.join(' | ')})` : '';

        const unitAmountCents = Math.round(item.price * 100);
        const qty = Math.max(1, item.quantity || 1);
        
        // Validate before returning
        if (!unitAmountCents || unitAmountCents <= 0) {
          console.error(`❌ Item ${index} tiene precio inválido:`, item.price);
        }
        
        return {
          name: (item.name + (description ? ` ${description}` : '')).trim() || 'Producto',
          unit_amount_cents: unitAmountCents,
          quantity: qty,
        };
      }).filter(item => item.unit_amount_cents > 0 && item.quantity > 0);
      
      console.log('✅ lineItems validados:', JSON.stringify(lineItems, null, 2));

      const totalCents = Math.round(order.total * 100);
      
      // Build final line_items with fallback if no valid items
      const finalLineItems = lineItems.length > 0 
        ? lineItems 
        : [{ name: 'Encargo Pastelería Reyes', unit_amount_cents: totalCents, quantity: 1 }];
      
      console.log('\n💳 ENVIANDO A STRIPE:');
      console.log('  📦 line_items:', JSON.stringify(finalLineItems, null, 2));
      console.log('  💰 total_cents:', totalCents);
      console.log('  🆔 orderId:', order.id);

      // Call Stripe checkout edge function with line_items
      const { data: stripeData, error: stripeError } = await supabase.functions.invoke("create-checkout", {
        body: {
          orderId: order.id,
          line_items: finalLineItems,
          total_cents: totalCents, // Fallback total
          successUrl: `${window.location.origin}/estado-pedido/${order.id}?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/checkout`,
        },
      });
      
      console.log('📡 Respuesta de create-checkout:');
      console.log('  ✅ stripeData:', JSON.stringify(stripeData, null, 2));
      console.log('  ❌ stripeError:', stripeError);

      if (stripeError) {
        // Try to extract detailed error from the response body
        let detailedError = stripeError.message || "Error desconocido";
        
        // Check if stripeError has context with response body
        if (stripeError.context && typeof stripeError.context.json === 'function') {
          try {
            const errorBody = await stripeError.context.json();
            if (errorBody?.error) {
              detailedError = errorBody.error;
              if (errorBody.type && errorBody.code) {
                detailedError += ` (${errorBody.type}/${errorBody.code})`;
              }
            }
          } catch (jsonErr) {
            console.error("[Checkout] Could not parse error body:", jsonErr);
          }
        }
        
        console.error("[Checkout] Stripe Edge Function error:", detailedError);
        
        toast({
          title: "Error de pago",
          description: detailedError,
          variant: "destructive",
        });
        return;
      }

      // Redirect to Stripe - DO NOT show confirmation yet
      if (stripeData?.url) {
        // Clear cart before redirect
        clearCart();
        sessionStorage.removeItem("encargoDetails");
        
        toast({
          title: "Redirigiendo al pago",
          description: "Te estamos llevando a la pasarela de pago segura...",
        });
        
        // Redirect in same tab to avoid popup blockers
        window.location.href = stripeData.url;
      } else {
        throw new Error("No se recibió la URL de pago");
      }
    } catch (error) {
      // Extract detailed error message for debugging
      const errorMessage = error instanceof Error 
        ? error.message 
        : (typeof error === 'object' && error !== null && 'message' in error) 
          ? String((error as { message: unknown }).message)
          : "Error desconocido";
      
      console.error("[Checkout] Unexpected payment error:", {
        message: errorMessage,
        fullError: JSON.stringify(error, null, 2)
      });
      
      toast({
        title: "Error de pago",
        description: `No se pudo iniciar el pago: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle free order (Test Mode) - bypass payment entirely
  const handleFreeOrder = async () => {
    setIsProcessing(true);
    try {
      const orderData = {
        order_type: (isEncargo ? "encargo" : (orderType || "llevar")) as "mesa" | "llevar" | "encargo",
        status: "pendiente" as const,
        payment_method: "local" as const, // Mark as local but will be free
        payment_status: "pagado" as const, // Already "paid" since it's free
        table_number: tableNumber,
        customer_name: encargo?.customerName,
        customer_phone: encargo?.contactType === "phone" ? encargo?.contactValue : null,
        customer_email: encargo?.contactType === "email" ? encargo?.contactValue : null,
        pickup_date: encargo?.pickupDate,
        pickup_time: encargo?.pickupTime,
        pickup_location: encargo?.location,
        observations: encargo?.observations ? `[PEDIDO DE PRUEBA] ${encargo.observations}` : "[PEDIDO DE PRUEBA]",
        location_code: orderLocationCode,
      };

      const { data, error } = await supabase.functions.invoke("validate-order", {
        body: {
          items: items.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            category: item.category,
            removedIngredients: item.removedIngredients,
          })),
          orderData,
        },
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || "Error al crear el pedido de prueba");
      }

      // Clear cart and encargo details
      clearCart();
      sessionStorage.removeItem("encargoDetails");

      toast({
        title: "🧪 ¡Pedido de prueba creado!",
        description: `Pedido #${data.order.order_number} registrado correctamente.`,
      });

      navigate(`/estado-pedido/${data.order.id}`);
    } catch (error) {
      console.error("Error creating free order:", error);
      toast({
        title: "Error",
        description: "No se pudo procesar el pedido de prueba.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };


  const handleSubmit = () => {
    // SECURITY: Block if mesa order without table number
    if (hasMesaError) {
      toast({
        title: "⚠️ Falta número de mesa",
        description: "Para pedir en mesa, por favor escanea el código QR de tu mesa.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    // VALIDATION: Collect missing fields for encargos
    if (isEncargo) {
      const missingFields: string[] = [];
      
      if (!encargo?.customerName || encargo.customerName.trim() === '') {
        missingFields.push("Nombre");
      }
      if (!encargo?.location) {
        missingFields.push("Tienda de recogida");
      }
      if (!encargo?.pickupDate) {
        missingFields.push("Fecha de recogida");
      }
      if (!encargo?.pickupTime) {
        missingFields.push("Hora de recogida");
      }
      
      // If there are missing fields, show toast and stop
      if (missingFields.length > 0) {
        toast({
          title: "⚠️ Datos incompletos",
          description: `Por favor completa: ${missingFields.join(', ')}`,
          variant: "destructive",
          duration: 5000,
        });
        return;
      }
    }

    // If total is 0 (Test Mode), bypass payment
    if (isFreeOrder) {
      handleFreeOrder();
      return;
    }
    
    if (paymentMethod === "online") {
      handlePayOnline();
    } else {
      handlePayLocal();
    }
  };

  if (items.length === 0) {
    return (
      <Layout>
        <section className="py-16 bg-background min-h-[60vh] flex items-center justify-center">
          <div className="container mx-auto px-4 text-center">
            <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h1 className="font-serif text-2xl font-bold text-primary mb-2">
              Tu cesta está vacía
            </h1>
            <p className="text-muted-foreground mb-6">
              Añade productos para continuar con tu pedido.
            </p>
            <Button onClick={() => navigate("/carta")} className="bg-secondary text-secondary-foreground">
              Ver la carta
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="bg-card py-6 border-b border-border">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-primary">
            Finalizar Pedido
          </h1>
        </div>
      </section>

      <section className="py-6 bg-background pb-32">
        <div className="container mx-auto px-4 max-w-2xl space-y-6">
          {/* Escaparate Mode Warning */}
          {isEscaparateMode && <EscaparateBanner variant="checkout" />}

          {/* Mesa Error Warning */}
          {hasMesaError && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700 font-medium">
                <AlertTriangle className="h-5 w-5" />
                Error: No se detecta número de mesa.
              </div>
              <p className="text-red-600 text-sm mt-1">
                Por favor, escanea el código QR de la mesa de nuevo.
              </p>
            </div>
          )}

          {/* Order Summary */}
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-primary text-lg">
                <ShoppingBag className="h-5 w-5 text-secondary" />
                Resumen del Pedido
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {items.map((item, index) => (
                <div key={index} className="flex justify-between items-start">
                  <div className="flex-1">
                    <span className="font-medium text-primary">{item.name}</span>
                    <span className="text-muted-foreground text-sm ml-2">x{item.quantity}</span>
                    {item.removedIngredients && item.removedIngredients.length > 0 && (
                      <p className="text-xs text-orange-600 font-medium mt-0.5">
                        ({item.removedIngredients.map(i => `Sin ${i}`).join(", ")})
                      </p>
                    )}
                  </div>
                  <span className="font-semibold text-secondary">
                    {formatPrice(item.price * item.quantity)}
                  </span>
                </div>
              ))}
              <Separator className="my-3" />
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-primary">Total</span>
                <span className="text-2xl font-bold text-secondary">{formatPrice(total)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Order Details */}
          {(orderType || encargo) && (
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <Clock className="h-5 w-5 text-secondary" />
                  Detalles del Pedido
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {orderType === "mesa" && tableNumber && (
                  <p className="flex items-center gap-2">
                    <span className="text-muted-foreground">Mesa:</span>
                    <span className="font-medium text-primary">{tableNumber}</span>
                  </p>
                )}
                {orderType === "llevar" && (
                  <p className="text-muted-foreground">📦 Pedido para llevar</p>
                )}
                {encargo && (
                  <>
                    <p className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-secondary" />
                      <span className="font-medium text-primary">{location?.name}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-secondary" />
                      <span className="font-medium text-primary">
                        {encargo.pickupDate} a las {encargo.pickupTime}
                      </span>
                    </p>
                    {encargo.customerName && (
                      <p className="text-muted-foreground">
                        Nombre: <span className="text-primary">{encargo.customerName}</span>
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Method - Only show if not a free order */}
          {isFreeOrder ? (
            <Card className="border-green-500 bg-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-green-700 text-lg">
                  <Gift className="h-5 w-5" />
                  Pedido de Prueba (0€)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-green-600 text-sm">
                  Este pedido es gratuito y se procesará sin requerir pago. 
                  Ideal para verificar el funcionamiento del sistema.
                </p>
              </CardContent>
            </Card>
          ) : isEncargo ? (
            /* ENCARGOS: Solo pago online - diseño premium */
            <Card className="border-secondary bg-secondary/5">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <CreditCard className="h-5 w-5 text-secondary" />
                  Método de Pago
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-secondary/10 border-2 border-secondary rounded-xl">
                  <div className="flex items-start gap-3">
                    <CreditCard className="w-8 h-8 text-secondary mt-0.5" />
                    <div>
                      <div className="font-semibold text-lg text-primary">Pago Online Seguro</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Procesado mediante Stripe. Tarjeta de crédito/débito, Apple Pay, Google Pay
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          🔒 Pago seguro
                        </span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          ✓ Confirmación inmediata
                        </span>
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                          📧 Recibo por email
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Los encargos requieren pago online para garantizar tu reserva
                </p>
              </CardContent>
            </Card>
          ) : (
            /* PEDIDOS NORMALES: Opciones de pago */
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <CreditCard className="h-5 w-5 text-secondary" />
                  Método de Pago
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={paymentMethod}
                  onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}
                  className="space-y-3"
                >
                  <div
                    className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all active:scale-[0.98] ${
                      paymentMethod === "local"
                        ? "border-secondary bg-secondary/10"
                        : "border-border hover:bg-muted/30"
                    } ${isEscaparateMode ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => !isEscaparateMode && setPaymentMethod("local")}
                  >
                    <RadioGroupItem value="local" id="local" disabled={isEscaparateMode} />
                    <Label htmlFor="local" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-3">
                        <Banknote className="h-6 w-6 text-secondary" />
                        <div>
                          <span className="font-medium text-primary block">Pago en local</span>
                          <span className="text-sm text-muted-foreground">
                            {isEscaparateMode ? "Requiere escanear QR en el local" : "Efectivo o tarjeta al recoger"}
                          </span>
                        </div>
                      </div>
                    </Label>
                  </div>
                  <div
                    className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all active:scale-[0.98] ${
                      paymentMethod === "online"
                        ? "border-secondary bg-secondary/10"
                        : "border-border hover:bg-muted/30"
                    }`}
                    onClick={() => setPaymentMethod("online")}
                  >
                    <RadioGroupItem value="online" id="online" />
                    <Label htmlFor="online" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-6 w-6 text-secondary" />
                        <div>
                          <span className="font-medium text-primary block">Pago online</span>
                          <span className="text-sm text-muted-foreground">Con tarjeta de crédito/débito</span>
                        </div>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {/* Submit Button - Always clickable, validates on click */}
          <Button
            size="lg"
            className={`w-full h-14 text-lg ${
              isProcessing
                ? "bg-muted hover:bg-muted text-muted-foreground"
                : isFreeOrder 
                  ? "bg-green-600 hover:bg-green-700 text-white" 
                  : "bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            }`}
            onClick={handleSubmit}
            disabled={isProcessing}
          >
            {isProcessing ? (
              "Procesando..."
            ) : isFreeOrder ? (
              <>
                <Gift className="h-5 w-5 mr-2" />
                Confirmar Pedido Gratuito
              </>
            ) : paymentMethod === "online" ? (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                Pagar {formatPrice(total)}
              </>
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Confirmar Pedido
              </>
            )}
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default Checkout;
